import React, { useEffect, useMemo, useRef, useState } from "react";
import Button from "../../shared/Button";
import { Spinner } from "reactstrap";
import { useSubjects, useSubjectsCore } from "../../../hooks/useSubjects";
import { useSubjectArea } from "../../../hooks/useSubjectArea";
import swal from "sweetalert";
import { UploadIcon } from "../../../assets/icons";
import { URL as url } from "../../../../../utils/Constants";
import Select from "../../shared/Select";
import CreatableSelect from "../../shared/CreatableSelect";

const SubjectForm = ({
  type,
  subject,
  setUpdateModal,
  updateSubject,
  isUpdating,
}) => {
  const { loading: isSubjectLoading, createSubject } = useSubjects();
  const fileInputRef = useRef(null);
  const { subjectArea, loading } = useSubjectArea();
  const { subjects, loading: subjectCoreLoading } = useSubjectsCore();
  const [filePreview, setFilePreview] = useState(null);
  const [formData, setFormData] = useState({
    subjectName: null,
    subjectTheory: true,
    subjectPractical: false,
    theoryCode: "",
    practicalCode: "",
    description: "",
    subjectArea: null,
    subjectImage: "",
  });

  const subjectAreaOption = useMemo(() => {
    if (!subjectArea || subjectArea?.length === 0) return [];
    return subjectArea.map((item) => ({ label: item.name, value: item.id }));
  }, [subjectArea]);

  const subjectListOption = useMemo(() => {
    if (!subjects || subjects?.length === 0) return [];
    return subjects?.map((item) => ({ label: item.name, value: item.name }));
  }, [subjects]);

  const handleChange = (e) => {
    const { name, type, value, files, checked } = e.target;

    if (files?.length > 0) {
      const file = files[0];

      if (file) {
        if (!file.type.startsWith("image/")) {
          swal("Oops", "Please upload a valid image file.");
          return;
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : files ? files[0] : value,
    }));

    if (files?.length > 0) {
      const preview = URL.createObjectURL(files[0]);
      setFilePreview(preview);
    }
  };

  const handleSubjectSubmit = async (e) => {
    e.preventDefault();

    if (!formData?.subjectName?.value.trim()) {
      swal("Oops", "Subject Name is required");
      return;
    }

    if (!formData.subjectTheory && !formData.subjectPractical) {
      swal("Oops", "Subject Type is required");
      return;
    }

    let payload = { ...formData };

    if (type === "edit") {
      if (!subject?.id) return;

      //setThumbnailPath if image is not changed
      if (subject.thumbnailPath === formData.subjectImage) {
        payload.thumbnailPath = formData.subjectImage;
      }

      try {
        await updateSubject(subject.id, payload);
        setUpdateModal();
      } catch (error) {
        console.error("Update Failed", error);
      }

      return;
    }

    try {
      await createSubject(payload);

      //reset data
      setFormData({
        subjectName: null,
        subjectTheory: true,
        subjectPractical: false,
        theoryCode: "",
        practicalCode: "",
        description: "",
        subjectArea: null,
        subjectImage: "",
      });
      setFilePreview(null);
    } catch (error) {
      console.error("Create Failed", error);
    }
  };

  useEffect(() => {
    if (type === "edit" && subject) {
      setFormData({
        subjectName: { label: subject.name, value: subject.name },
        subjectTheory: subject.hasTheory,
        subjectPractical: subject.hasPractical,
        theoryCode: subject.subjectTheoryCode,
        practicalCode: subject.subjectPracticalCode,
        description: subject.description,
        subjectArea: subject.subjectAreaId
          ? {
              label: subject.subjectAreaName,
              value: subject.subjectAreaId,
            }
          : null,
        subjectImage: subject.thumbnailPath,
      });
    }
  }, [subject, type]);

  useEffect(() => {
    return () => {
      if (filePreview) {
        URL.revokeObjectURL(filePreview);
      }
    };
  }, [filePreview]);

  return (
    <form
      onSubmit={handleSubjectSubmit}
      className={`arr-form arr-subject-setup-form ${type === "edit" ? "noShadow" : ""}`}
      style={type !== "edit" ? { width: "600px" } : {}}
    >
      <div className="form-input">
        <label htmlFor="subjectName" className="required">
          Subject Name
        </label>
        <CreatableSelect
          name={"subjectName"}
          options={subjectListOption}
          isLoading={subjectCoreLoading}
          value={formData.subjectName || null}
          placeholder="Select subject name"
          isSearchable={true}
          styles={{
            control: { width: "100%" },
          }}
          onChange={(option) =>
            setFormData((prev) => ({ ...prev, subjectName: option }))
          }
        />
      </div>
      <div className="form-input">
        <p style={{ display: "inline-block" }} className="label required">
          Subject Type
        </p>
        <div className="checkbox-wrapper">
          <div className="checkbox">
            <input
              checked={formData.subjectTheory}
              type="checkbox"
              name="subjectTheory"
              id="subjectTheory"
              className="form-control"
              disabled={subject ? !subject?.canModify : false}
              onChange={handleChange}
            />
            <label htmlFor="subjectTheory">Theory</label>
          </div>
          <div className="checkbox">
            <input
              type="checkbox"
              checked={formData.subjectPractical}
              name="subjectPractical"
              className="form-control"
              id="subjectPractical"
              disabled={subject ? !subject?.canModify : false}
              onChange={handleChange}
            />
            <label htmlFor="subjectPractical">Practical</label>
          </div>
        </div>
        {!subject?.canModify && type === "edit" && (
          <div className="arr-note">
            <strong>Note: </strong>You cannot edit the subject type because this
            subject is already configured in the assessment.
          </div>
        )}
      </div>
      {formData.subjectTheory && (
        <div className="form-input">
          <label htmlFor="theoryCode">Theory Code</label>
          <input
            type="text"
            name="theoryCode"
            id="theoryCode"
            value={formData.theoryCode}
            className="form-control"
            onChange={handleChange}
          />
        </div>
      )}
      {formData.subjectPractical && (
        <div className="form-input">
          <label htmlFor="practicalCode">Practical Code</label>
          <input
            type="text"
            name="practicalCode"
            id="practicalCode"
            value={formData.practicalCode}
            className="form-control"
            onChange={handleChange}
          />
        </div>
      )}
      <div className="form-input">
        <label htmlFor="description">Description</label>
        <input
          type="text"
          name="description"
          id="description"
          value={formData.description}
          className="form-control"
          onChange={handleChange}
        />
      </div>
      <div className="form-input">
        <label htmlFor="subjectArea">Subject Area</label>
        <Select
          options={subjectAreaOption}
          isLoading={loading.read}
          value={formData.subjectArea || null}
          placeholder="Select subject area"
          name="subjectArea"
          styles={{
            control: { width: "100%" },
          }}
          onChange={(option) =>
            setFormData((prev) => ({ ...prev, subjectArea: option }))
          }
          isSearchable={true}
        />
      </div>
      <div className="form-input">
        <label htmlFor="subjectImage">Subject Image</label>
        <input
          ref={fileInputRef}
          type="file"
          name="subjectImage"
          id="subjectImage"
          className="form-control"
          onChange={handleChange}
          style={{ display: "none" }}
          accept=".jpg, .jpeg, .png, .webp"
        />
        <div
          id="subjectImage"
          onClick={() => fileInputRef.current.click()}
          className="file-input"
        >
          {formData.subjectImage ? (
            <Button
              className={"file-remove-btn"}
              variant="danger"
              onClick={() => {
                setFormData((prev) => ({ ...prev, subjectImage: "" }));
                setFilePreview(null);
              }}
            >
              Remove
            </Button>
          ) : (
            <UploadIcon
              style={{ width: "32px", height: "32px", color: "#adb0b8" }}
            />
          )}
        </div>

        {filePreview ? (
          <img src={filePreview} className="file-preview" alt="file-preview" />
        ) : type === "edit" && formData.subjectImage && !filePreview ? (
          <img
            src={url.imageSelectURL + formData.subjectImage}
            className="file-preview"
            alt="file-preview"
          />
        ) : null}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          justifyContent: "end",
        }}
      >
        <Button
          ariaLabel="create-edit-btn"
          disabled={isSubjectLoading.create || isUpdating}
          type="submit"
        >
          {isSubjectLoading.create || isUpdating ? (
            <Spinner size={"sm"} color="white" />
          ) : type === "edit" ? (
            "Update"
          ) : (
            "Create"
          )}
        </Button>
      </div>
    </form>
  );
};

export default SubjectForm;
